import { Box, VStack } from '@web/components/atoms/Layout'
import { Text } from '@web/components/atoms/Typography'
import PageCheckBox from '../../DonationForm/components/atoms/PageCheckBox'
import { CustomFieldComponentProps } from '../CustomFieldComponent'

const toSelectedValues = (value: CustomFieldComponentProps['value']) => {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean)
  }

  if (typeof value !== 'string' || !value) {
    return []
  }

  return value.split(',').filter(Boolean)
}

const MultiSelect = ({
  id,
  name,
  label,
  acceptedValues,
  onChange,
  value,
  isRequired,
  fieldDescription,
}: CustomFieldComponentProps) => {
  const selected = toSelectedValues(value)
  const labelId = `custom-field-${name}-label`

  const update = (optionValue: string, checked: boolean) => {
    const next = new Set(selected)
    if (checked) {
      next.add(optionValue)
    } else {
      next.delete(optionValue)
    }

    const ordered = (acceptedValues ?? [])
      .map((option) => option.value)
      .filter((candidate) => next.has(candidate))

    onChange?.({
      target: {
        name: id,
        value: ordered.join(','),
      },
    } as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <VStack alignItems="start" gap="8px" w="100%" role="group" aria-labelledby={labelId}>
      <Text id={labelId} fontSize="sm">
        {label}
        {isRequired ? <span style={{ color: 'red' }}> *</span> : null}
      </Text>
      {fieldDescription ? (
        <Text fontSize="xs" color="ge.text.secondary">
          {fieldDescription}
        </Text>
      ) : null}
      <Box position="relative" w="100%">
        <input
          aria-required={isRequired || undefined}
          aria-hidden="true"
          tabIndex={-1}
          readOnly
          value={selected.join(',')}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            border: 0,
          }}
        />
        <VStack alignItems="start" gap="8px">
          {(acceptedValues ?? []).map((option) => (
            <PageCheckBox
              key={option.key}
              id={`custom-field-${name}-${option.key}`}
              name={`${name}-${option.key}`}
              label={option.value}
              isChecked={selected.includes(option.value)}
              onChange={(checked) => update(option.value, checked)}
            />
          ))}
        </VStack>
      </Box>
    </VStack>
  )
}

export default MultiSelect
